/**
 * Route-name → i18n-key-prefix map consumed by the router guard.
 *
 * Each entry maps a route `name` to the i18n prefix that holds its
 * `.title`, `.description`, and `.breadcrumb` strings. The guard resolves
 * the three keys on every navigation and writes them to `document.title`,
 * the `routeHead` state, and `to.meta` — so route definitions stay free of
 * i18n boilerplate.
 *
 * Adding a new page? Give the route a stable `name` and register the
 * i18n prefix here. Routes without an entry are left untouched and can
 * fall back to static meta if needed.
 */
export const routeI18nKeys: Record<string, string> = {
  // Core
  'core.dashboard': 'meta.pages.dashboard',
  'core.personalSettings': 'meta.pages.personalSettings',

  // Auth
  'auth.register': 'meta.pages.auth.register',
  'auth.login': 'meta.pages.auth.login',
  'auth.forgotPassword': 'meta.pages.auth.forgotPassword',
  'auth.resetPassword': 'meta.pages.auth.resetPassword',

  // Admin
  'admin.settings': 'meta.pages.admin.settings',

  // Questions
  'questions.categories': 'meta.pages.questions.categories',
  'questions.overview': 'meta.pages.questions.overview',
  'questions.detail': 'meta.pages.questions.detail',
  'questions.detail.default': 'meta.pages.questions.detail',
  'questions.detail.gatekeeper': 'meta.pages.questions.gatekeeper',
  'questions.detail.options': 'meta.pages.questions.options',
  'questions.detail.followups': 'meta.pages.questions.followups',
  'questions.optionsPage': 'meta.pages.questions.options',

  // Evaluations
  'evaluations.overview': 'meta.pages.evaluations.overview',
  'evaluations.detail': 'meta.pages.evaluations.detail',
  'evaluations.default': 'meta.pages.evaluations.default',
  'evaluations.editCondition': 'meta.pages.evaluations.editCondition',

  // Flags
  'flags.overview': 'meta.pages.flags.overview',
  'flags.detail': 'meta.pages.flags.detail',

  // Reports
  'reports.overview': 'meta.pages.reports.overview',
  'debug.question': 'meta.pages.debug.question',

  // Tenant
  'tenant.info': 'meta.pages.tenant.info',
  'tenant.layout': 'meta.pages.tenant.layout',
  'tenant.translations.overview': 'meta.pages.tenant.translations.overview',
  'tenant.translations.language': 'meta.pages.tenant.translations.language',
  'tenant.translations.questionsOptions': 'meta.pages.tenant.translations.questionsOptions',
  'tenant.translations.questionDetail': 'meta.pages.tenant.translations.questionDetail',
  'tenant.translations.optionsDetail': 'meta.pages.tenant.translations.optionsDetail',
  'tenant.translations.faq.overview': 'meta.pages.tenant.translations.faq.overview',
  'tenant.translations.faq.qa': 'meta.pages.tenant.translations.faq.qa',
  'tenant.translations.faq.qaDetail': 'meta.pages.tenant.translations.faq.qaDetail',
  'tenant.translations.faq.categories': 'meta.pages.tenant.translations.faq.categories',
  'tenant.translations.faq.categoryEdit': 'meta.pages.tenant.translations.faq.categoryEdit',
  'tenant.translations.evaluation.overview': 'meta.pages.tenant.translations.evaluation.overview',
  // Preserved literal name — referenced via router.push()
  EvaluationCategoryDetailView: 'meta.pages.tenant.translations.evaluation.detail',
  'tenant.translations.staticTexts.overview': 'meta.pages.tenant.translations.staticTexts.overview',
  // Preserved literal name — referenced via router.push()
  StaticTextsDetailView: 'meta.pages.tenant.translations.staticTexts.detail',
  'tenant.settings.section': 'meta.pages.tenant.settings.overview',
  'tenant.settings.overview': 'meta.pages.tenant.settings.overview',
  'tenant.settings.general': 'meta.pages.tenant.settings.general',
  'tenant.settings.modules': 'meta.pages.tenant.settings.modules',
  'tenant.settings.translations': 'meta.pages.tenant.settings.translations',

  // FAQ module
  'faq.overview': 'faq.routeMeta.overview',
  'faq.settings': 'faq.routeMeta.settings',
  'faq.faqs': 'faq.routeMeta.faqs',
  'faq.faqDetail': 'faq.routeMeta.faqDetail',
  'faq.categories': 'faq.routeMeta.categories',
  'faq.categoryDetail': 'faq.routeMeta.categoryDetail',
  'faq.translations': 'faq.routeMeta.translations',
  'faq.translationFaqs': 'faq.routeMeta.translationFaqs',
  'faq.translationFaqDetail': 'faq.routeMeta.translationFaqDetail',
  'faq.translationCategories': 'faq.routeMeta.translationCategories',

  // FAQ legacy core (duplicates of module routes; registered first,
  // module routes are shadowed for matching — kept for backwards
  // compatibility until the old core routes are removed)
  'coreFaq.overview': 'meta.pages.faq.overview',
  'coreFaq.faqs': 'meta.pages.faq.faqs.overview',
  'coreFaq.faqDetail': 'meta.pages.faq.faqs.detail',
  'coreFaq.categories': 'meta.pages.faq.categories.overview',
  'coreFaq.categoryDetail': 'meta.pages.faq.categories.detail',
  'coreFaq.translations': 'meta.pages.faq.translations.overview',
  'coreFaq.translationFaqs': 'meta.pages.faq.translations.faqs',
  'coreFaq.translationFaqDetail': 'meta.pages.faq.translations.faqDetail',
  'coreFaq.translationCategories': 'meta.pages.faq.translations.categories',
  'coreFaq.translationCategoryDetail': 'meta.pages.faq.translations.categoryDetail',

  // Lead-generation module
  'leadgen.section': 'leadgen.routeMeta.section',
  'leadgen.overview': 'leadgen.routeMeta.overview',
  'leadgen.settings': 'leadgen.routeMeta.settings',
  'leadgen.privacySettings': 'leadgen.routeMeta.privacySettings',
  'leadgen.mailTemplate': 'leadgen.routeMeta.mailTemplate'
}
